import { EFinancingProgram } from '@if/core/types'
import * as yup from '@if/core/yup'
import { Typography } from '@mui/material'
import useFeatureFlags from '@src/data/api/featureSwitch-api/featureSwitch-api'
import { FinancingConfigDto } from '@src/data/api/financing-config-api'
import {
  MEDICARD_SERVICE_CATEGORIES,
  PERSONAL_MAX_LOAN_AMOUNT_DEFAULT,
  PERSONAL_MAX_LOAN_AMOUNT_MEDICARD,
} from '@src/data/types/Constants'
import { FC } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import FinancingProgramConfigsInputs from './FinancingProgramConfigsInputs'

function getEffectiveMaxLoanAmount(
  serviceCategory: string | undefined,
  financingProgramId: EFinancingProgram,
  defaultMaxLoanAmount: number,
): number {
  if (financingProgramId !== EFinancingProgram.Personal) {
    return defaultMaxLoanAmount
  }

  const isMedicard = serviceCategory ? MEDICARD_SERVICE_CATEGORIES.includes(serviceCategory) : false

  if (isMedicard) {
    return PERSONAL_MAX_LOAN_AMOUNT_MEDICARD
  }

  return PERSONAL_MAX_LOAN_AMOUNT_DEFAULT
}

function makeProgramConfigShape(
  financingConfig: FinancingConfigDto | undefined,
  effectiveMaxLoanAmount: number,
) {
  const shape = {
    minLoanAmount: yup.number().nullable(),
    maxLoanAmount: yup.number().nullable(),
    maxLoanTerm: yup.number().nullable(),
  }

  if (financingConfig?.minimumLoanAmount)
    shape.minLoanAmount = shape.minLoanAmount
      .min(financingConfig?.minimumLoanAmount)
      .max(effectiveMaxLoanAmount)

  if (effectiveMaxLoanAmount)
    shape.maxLoanAmount = shape.maxLoanAmount
      .when('minLoanAmount', {
        is: (value: number | null | undefined) => value !== null && value !== undefined,
        then: (schema) => schema.min(yup.ref('minLoanAmount')),
        otherwise: (schema) => schema.min(financingConfig?.minimumLoanAmount ?? 0),
      })
      .max(effectiveMaxLoanAmount)

  if (financingConfig?.maxLoanTerm)
    shape.maxLoanTerm = shape.maxLoanTerm.min(financingConfig?.minLoanTerm).max(financingConfig?.maxLoanTerm)

  return yup.object().shape(shape).optional()
}

export function makeMerchantFinancingProgramConfigsSchema(
  financingConfigs: FinancingConfigDto[],
  serviceCategory: string | undefined,
) {
  const shape = Object.values(EFinancingProgram).reduce(
    (shape, program) => {
      const financingConfig = financingConfigs.find((c) => c.financingProgramId === program)
      const effectiveMaxLoanAmount = getEffectiveMaxLoanAmount(
        serviceCategory,
        program,
        financingConfig?.maximumLoanAmount ?? 0,
      )
      shape[program] = makeProgramConfigShape(financingConfig, effectiveMaxLoanAmount)
      return shape
    },
    {} as Record<EFinancingProgram, yup.ObjectSchema<yup.InferType<ReturnType<typeof makeProgramConfigShape>>>>,
  )

  return yup.object().shape({
    financingProgramConfigs: yup.object().shape(shape),
  })
}

export type MerchantFinancingProgramConfigsSchemaType = yup.InferType<
  ReturnType<typeof makeMerchantFinancingProgramConfigsSchema>
>

interface MerchantFinancingProgramConfigsPanelProps {
  supportedFinancingPrograms: EFinancingProgram[]
  financingConfigs: FinancingConfigDto[]
  serviceCategory?: string
}

const MerchantFinancingProgramConfigsPanel: FC<MerchantFinancingProgramConfigsPanelProps> = ({
  supportedFinancingPrograms,
  financingConfigs,
  serviceCategory,
}) => {
  const { t } = useTranslation()
  const { control } = useFormContext<MerchantFinancingProgramConfigsSchemaType>()
  const [featureFlags] = useFeatureFlags()

  return (
    <>
      <Typography variant="h6" gutterBottom paddingBottom={2}>
        {t('merchant.financingProgramConfigs')}
      </Typography>
      {supportedFinancingPrograms.map((programId) => {
        let disabled: boolean
        if (featureFlags?.permitChangeFinancingProgramsSettingsForPersonalMerchant) {
          disabled = programId !== EFinancingProgram.B2c && programId !== EFinancingProgram.Personal
        } else {
          disabled = programId !== EFinancingProgram.B2c
        }
        const financingConfig = financingConfigs.find((c) => c.financingProgramId === programId)
        const effectiveMaxLoanAmount = getEffectiveMaxLoanAmount(
          serviceCategory,
          programId,
          financingConfig?.maximumLoanAmount ?? 0,
        )
        return (
          <FinancingProgramConfigsInputs
            key={programId}
            financingProgramId={programId}
            control={control}
            financingConfig={financingConfig}
            disabled={disabled}
            effectiveMaxLoanAmount={effectiveMaxLoanAmount}
          />
        )
      })}
    </>
  )
}

export default MerchantFinancingProgramConfigsPanel
